import { useState, useEffect, useCallback } from 'react';
import { cookieConsent, CookieConsentData, CookieCategory } from '@/utils/cookieConsent';

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsentData | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize consent state
    const currentConsent = cookieConsent.getConsent();
    setConsent(currentConsent);
    setShowBanner(!currentConsent);
    setIsLoading(false);

    // Listen for consent changes
    const handleConsentChange = (newConsent: CookieConsentData) => {
      setConsent(newConsent);
      setShowBanner(false);
    };

    cookieConsent.addListener(handleConsentChange);

    // Cleanup listener on unmount
    return () => {
      cookieConsent.removeListener(handleConsentChange);
    };
  }, []);

  const acceptAll = useCallback(() => {
    cookieConsent.acceptAll();
  }, []);

  const acceptNecessary = useCallback(() => {
    cookieConsent.acceptNecessary();
  }, []);

  const saveCustomConsent = useCallback((customConsent: Omit<CookieConsentData, 'timestamp' | 'version'>) => {
    cookieConsent.saveConsent(customConsent);
  }, []);

  const updateConsent = useCallback(() => {
    setShowBanner(true);
  }, []);

  const isCategoryAllowed = useCallback((category: CookieCategory) => {
    return cookieConsent.isCategoryAllowed(category);
  }, []);

  const hasConsent = useCallback(() => {
    return cookieConsent.hasConsent();
  }, []);

  return {
    consent,
    showBanner,
    isLoading,
    acceptAll,
    acceptNecessary,
    saveCustomConsent,
    updateConsent,
    isCategoryAllowed,
    hasConsent,
    cookieDescriptions: cookieConsent.getCookieDescriptions()
  };
}
